/**
 * Motivierende (und leicht schmunzelnde) Sprüche für die Login-Seite. Bei
 * jedem Seitenaufruf ein zufälliger Pick aus dem Pool — passend zur aktuellen
 * Tageszeit + Jahreszeit. Sprüche ohne Kontext-Tag sind immer im Pool.
 *
 * Ton: professionell, branchenkundig, aber nicht bierernst. Kein Insider-
 * Scherz, der die lesende Person verunsichert.
 *
 * Sprache: konsequent genderneutral. Keine Rollenbezeichnungen im Singular
 * (kein „der Bauherr", keine „die Projektleiterin"); stattdessen Bezug auf
 * Tätigkeiten, Artefakte oder allgemeine Personalpronomen („wer …", „Sie").
 * Bei Änderungen bitte gegenprüfen.
 *
 * Pool-Größe: ~200 Sprüche, verteilt über Tages- und Jahreszeiten.
 */

type Tageszeit = 'morgen' | 'tag' | 'abend' | 'nacht';
type Jahreszeit = 'fruehling' | 'sommer' | 'herbst' | 'winter';

interface Slogan {
  text: string;
  tageszeit?: Tageszeit;
  jahreszeit?: Jahreszeit;
}

const SLOGANS: Slogan[] = [
  // --- Generisch (immer im Pool) ------------------------------------------
  { text: 'Termine, Kosten, Qualität — such dir zwei aus. Beim Dritten helfen wir.' },
  { text: 'Planen schlägt Improvisieren. Meistens.' },
  { text: 'Nachträge sind die zweite Kalkulation. Wir rechnen beide mit.' },
  { text: 'HOAI-Paragraphen sind keine Gedichte. Wer sie kennt, wird trotzdem bewundert.' },
  { text: 'Kostensicherheit ist kein Schlagwort. Sondern ein Dashboard.' },
  { text: 'Saubere Abrechnung beginnt beim ersten Spatenstich.' },
  { text: 'Ein gutes Projekt hat zwei Pläne: den offiziellen und den, der hält.' },
  { text: 'Leistungsstand heißt nicht, was gemacht ist — sondern was belegbar ist.' },
  { text: 'Baukosten kennen zwei Richtungen: hoch und hoch.' },
  { text: 'Der günstigste Angebotspreis ist der teuerste Lehrmeister.' },
  { text: 'Jede Baustelle hat ein Budget. Nicht jede Baustelle hält es.' },
  { text: 'Ordnung ist die halbe Miete. Das Honorar ist die andere.' },
  { text: 'Die HOAI ist wie ein gutes Gewürz: sparsam dosiert, aber unverzichtbar.' },
  { text: 'Eine saubere Kostenverfolgung ist die beste Prävention.' },
  { text: 'Wer die Leistungsphasen kennt, kennt das Projekt.' },
  { text: 'Kostengruppen sind wie Schubladen: leer nur, wenn niemand aufräumt.' },
  { text: 'Ein Plan, der funktioniert, ist kein Wunder — sondern gute Arbeit.' },
  { text: 'Honorar ist kein Thema. Wenn es richtig kalkuliert ist.' },
  { text: 'Gute Dokumentation spart drei Nachfragen und zwei Nachträge.' },
  { text: 'Bauen ist Teamsport. Auch wenn das Gehalt einzeln ausgezahlt wird.' },
  { text: 'Transparenz kostet eine Minute. Nicht-Transparenz eine Woche.' },
  { text: 'Kostenstand aktualisiert — Gewissen entlastet.' },
  { text: 'Zwischen Vorentwurf und Abnahme liegen Welten. Und Excel-Tabellen.' },
  { text: 'Wer Nachträge versteht, hält Fristen.' },
  { text: 'Die KG400 ist da, wo die Diskussionen beginnen.' },
  { text: 'Jede Planung ist besser als keine. Unsere ist sogar prüfbar.' },
  { text: 'Zahlen lügen nicht. Sie sind nur manchmal unhöflich.' },
  { text: 'Ein sauberes Budget ist der beste Streitschlichter.' },
  { text: 'Ein Projekt ohne Kostenverfolgung ist ein Roman ohne Seitenzahlen.' },
  { text: 'Wer keine Kostenstufe pflegt, feiert später Überraschungsparties.' },
  { text: 'DIN 276 ist keine Diät — sondern die saubere Gliederung.' },
  { text: 'Leistungsphasen: neun Kapitel, ein Happy End.' },
  { text: 'Kostenkontrolle ist kein Sport, aber sie hält fit.' },
  { text: 'Die beste Bauzeit ist die, die eingehalten wird.' },
  { text: 'Gute Projekte entstehen an drei Stellen: Plan, Baustelle und Excel.' },
  { text: 'Ein Nachtrag am Anfang ist günstiger als eine Überraschung am Ende.' },
  { text: 'Wer heute loggt, spart morgen die Suche.' },
  { text: 'Die HOAI ist ein Kompass, kein Korsett.' },
  { text: 'Zeit ist Geld. Bauzeit ist Mehrgeld.' },
  { text: 'Schönheit zählt. Aber bezahlt wird nach Leistung.' },
  { text: 'Zuverlässigkeit ist die teuerste Ressource. Und sie rechnet sich.' },
  { text: 'Gutes Projektmanagement erkennt man am Schweigen der Baustelle.' },
  { text: 'Eine Kostenstufe ist kein Stempel, sondern ein Versprechen.' },
  { text: 'Die Baustelle ist die ehrlichste Qualitätsprüfung.' },
  { text: 'Ein Projekt ohne Risiken wäre schön. Gibt es aber nicht.' },
  { text: 'Planung ersetzt kein Talent. Aber sie ersetzt viel Verdruss.' },
  { text: 'Die beste Kostenprognose ist eine gepflegte Datenlage.' },
  { text: 'Gute Projekte haben keine Überraschungen. Sondern dokumentierte Abweichungen.' },
  { text: 'Wer Kosten kennt, verhandelt besser.' },
  { text: 'Eine Woche Planung spart drei Monate Unordnung.' },
  { text: 'Bauprojekte sind wie Wetter: niemand kann sie ganz vorhersagen, aber gute Modelle helfen.' },

  // --- Morgen (05-10 Uhr) -------------------------------------------------
  { text: 'Jeder Bauplan beginnt mit einem Kaffee und einem Risiko.', tageszeit: 'morgen' },
  { text: "Morgenstund' hat Honorar im Mund.", tageszeit: 'morgen' },
  { text: 'Ein guter Tag beginnt mit einem sortierten Leistungsstand.', tageszeit: 'morgen' },
  { text: 'Zeitig aufstehen macht die Baustelle nicht kürzer — aber kalkulierbarer.', tageszeit: 'morgen' },
  { text: 'Frühes Vogelgezwitscher, früher Baulärm. Beides unvermeidlich.', tageszeit: 'morgen' },
  { text: 'Die ersten 20 Minuten gehören dem Dashboard. Dann dem Telefon.', tageszeit: 'morgen' },
  { text: 'Guten Morgen. Die KG300 wartet nicht.', tageszeit: 'morgen' },
  { text: 'Wer morgens plant, wird nachmittags nicht überrascht.', tageszeit: 'morgen' },
  { text: 'Ein Bauzeitenplan ist schnell erstellt. Aber nicht vor dem ersten Kaffee.', tageszeit: 'morgen' },
  { text: 'Früh am Tag: der beste Moment für eine ehrliche Kostenprognose.', tageszeit: 'morgen' },
  { text: 'Baubesprechung vor neun? Ambitioniert. Und üblich.', tageszeit: 'morgen' },
  { text: 'Der Morgen gehört den offenen Punkten.', tageszeit: 'morgen' },
  { text: 'Heute sortieren. Morgen abrechnen.', tageszeit: 'morgen' },
  { text: 'Wer frühmorgens plant, kann mittags entscheiden.', tageszeit: 'morgen' },
  { text: 'Der Bauzaun steht. Ab jetzt zählt jede Minute.', tageszeit: 'morgen' },
  { text: 'Erst die Kostenstufe, dann die E-Mails.', tageszeit: 'morgen' },
  { text: 'Frische Zahlen sind wie frische Brötchen: morgens am besten.', tageszeit: 'morgen' },
  { text: 'Morgens ist die Baustelle noch unschuldig.', tageszeit: 'morgen' },
  { text: 'Ein klarer Kopf plus eine klare Datenlage — unschlagbar.', tageszeit: 'morgen' },
  { text: 'Der erste Blick ins Dashboard schlägt den ersten Blick ins Postfach.', tageszeit: 'morgen' },
  { text: 'Vor der ersten Besprechung: die letzten Zahlen prüfen.', tageszeit: 'morgen' },
  { text: 'Guten Morgen. Neue Zahlen, neue Chancen.', tageszeit: 'morgen' },
  { text: 'Früh übt sich, wer ein sauberes Projekt werden will.', tageszeit: 'morgen' },
  { text: 'Der Tag beginnt mit dem, was gestern offen blieb.', tageszeit: 'morgen' },
  { text: 'Wer den Morgen strukturiert, spart den Nachmittag.', tageszeit: 'morgen' },

  // --- Tag (11-16 Uhr) ----------------------------------------------------
  { text: 'Mittagszeit. Gute Gelegenheit, den Kostenrahmen zu prüfen.', tageszeit: 'tag' },
  { text: 'Zwischen zwei Terminen: ein kurzer Blick auf die Kostenstufe.', tageszeit: 'tag' },
  { text: 'Mittagspause ist der beste Moment für den zweiten Kaffee und den dritten Nachtrag.', tageszeit: 'tag' },
  { text: 'Der Nachmittag liebt klare Entscheidungen.', tageszeit: 'tag' },
  { text: 'Zwischen 13 und 15 werden die wichtigsten Freigaben erteilt.', tageszeit: 'tag' },
  { text: 'Mittagslicht klärt nicht nur den Bauplatz. Auch das Budget.', tageszeit: 'tag' },
  { text: 'Am Mittag ist die Baustelle am lautesten. Und das Budget am nervösesten.', tageszeit: 'tag' },
  { text: 'Jour fixe am Nachmittag? Dann lieber mit aktuellen Zahlen.', tageszeit: 'tag' },
  { text: 'Mittag: Pause für den Magen, nicht fürs Projekt.', tageszeit: 'tag' },
  {
    text: 'Die besten Entscheidungen fallen zwischen Tür und Angel. Oder zwischen Fenster und Fassade.',
    tageszeit: 'tag',
  },
  { text: 'Nachmittags wird gerechnet. Und entschieden.', tageszeit: 'tag' },
  { text: 'Mittagszeit ist Dashboard-Zeit.', tageszeit: 'tag' },
  { text: 'Um 14 Uhr: kurz durchatmen, dann die Kostenstand prüfen.', tageszeit: 'tag' },
  { text: 'Der Nachmittag entscheidet, was der Tag wert war.', tageszeit: 'tag' },
  { text: 'Wer mittags nachrechnet, kann abends ruhig schlafen.', tageszeit: 'tag' },

  // --- Abend (17-22 Uhr) --------------------------------------------------
  { text: 'Feierabend nähert sich. Die Kosten sind aber noch nicht im Feierabend.', tageszeit: 'abend' },
  { text: 'Wer abends sauber abrechnet, schläft morgens ruhiger.', tageszeit: 'abend' },
  { text: 'Noch im Büro? Die KG400 auch.', tageszeit: 'abend' },
  { text: 'Abendstunde, Dokumentationsstunde.', tageszeit: 'abend' },
  { text: 'Ein letzter Blick aufs Dashboard, dann ist Feierabend.', tageszeit: 'abend' },
  { text: 'Am Abend zeigt sich, was der Tag geschafft hat.', tageszeit: 'abend' },
  { text: 'Die Baustelle ist still. Die Daten sind ruhig. Gut so.', tageszeit: 'abend' },
  { text: 'Wer den Tag gut dokumentiert, gewinnt den nächsten.', tageszeit: 'abend' },
  { text: 'Abends ist die beste Zeit für Struktur.', tageszeit: 'abend' },
  { text: 'Abendruhe auf der Baustelle. Aber nicht in der Abrechnung.', tageszeit: 'abend' },
  { text: 'Zum Tagesausklang: die offenen Punkte auf morgen.', tageszeit: 'abend' },
  { text: 'Feierabend ist dann, wenn alle Zahlen stimmen.', tageszeit: 'abend' },
  { text: 'Nachrichten von der Baustelle? Heute nicht mehr.', tageszeit: 'abend' },
  { text: 'Ein ruhiger Abend nach einem geplanten Tag.', tageszeit: 'abend' },
  { text: 'Abends werden Projekte sortiert, nicht gebaut.', tageszeit: 'abend' },
  { text: 'Am Abend ist die Datenlage meistens ehrlicher als am Morgen.', tageszeit: 'abend' },
  { text: 'Ein gutes Projekt endet jeden Tag mit einem aktuellen Stand.', tageszeit: 'abend' },
  { text: 'Abendsonne auf dem Gerüst — Projekte lieben solche Momente.', tageszeit: 'abend' },
  { text: 'Der Abend gehört der Dokumentation. Und vielleicht einem Feierabendgetränk.', tageszeit: 'abend' },
  { text: 'Nach 18 Uhr ist die Tabelle oft ehrlicher.', tageszeit: 'abend' },
  { text: 'Noch hier? Dann ist das Projekt wahrscheinlich wichtig.', tageszeit: 'abend' },
  { text: 'Zum Feierabend: ein sauberer Leistungsstand.', tageszeit: 'abend' },
  { text: 'Die Sonne geht unter. Die Abrechnung nicht.', tageszeit: 'abend' },
  { text: 'Ein aufgeräumtes Dashboard ist der beste Feierabend.', tageszeit: 'abend' },
  { text: 'Feierabend-Regel: nichts offen, nichts offen.', tageszeit: 'abend' },

  // --- Nacht (23-4 Uhr) ---------------------------------------------------
  { text: 'Noch wach? Dann ist das Projekt wahrscheinlich spannend.', tageszeit: 'nacht' },
  { text: 'Nachtrag um Mitternacht — willkommen im Bauprojekt.', tageszeit: 'nacht' },
  { text: 'Die Baustelle schläft. Die Zahlen nicht.', tageszeit: 'nacht' },
  { text: 'Wer nachts rechnet, rechnet mutig.', tageszeit: 'nacht' },
  { text: 'Nachtaktiv? Die HOAI respektiert das.', tageszeit: 'nacht' },
  { text: 'Mitternacht ist selten die beste Stunde für eine Freigabe. Aber manchmal notwendig.', tageszeit: 'nacht' },
  { text: 'Späte Stunde. Ruhige Daten. Gute Zeit.', tageszeit: 'nacht' },
  { text: 'Um diese Zeit zählt nur eins: ein guter Stand auf dem Bildschirm.', tageszeit: 'nacht' },
  { text: 'Nachteulen bauen die saubersten Dokumentationen.', tageszeit: 'nacht' },
  { text: 'Nachts ist jede Baustelle ein Versprechen.', tageszeit: 'nacht' },
  { text: 'Wenn alle schlafen, werden die besten Pläne verfeinert.', tageszeit: 'nacht' },
  { text: 'Die Nacht ist die Freundin strukturierter Arbeit.', tageszeit: 'nacht' },
  { text: 'Ein ruhiger Gedanke um 23 Uhr schlägt zehn hektische am Morgen.', tageszeit: 'nacht' },
  { text: 'Nach Mitternacht ist der Terminplan meistens ehrlich.', tageszeit: 'nacht' },
  { text: 'Gute Planung braucht Ruhe. Die findet sich oft erst spät.', tageszeit: 'nacht' },

  // --- Frühling (März, April, Mai) -----------------------------------------
  { text: 'Mit dem ersten Krokus beginnt die Rohbausaison.', jahreszeit: 'fruehling' },
  { text: 'Frühjahrsputz gilt auch fürs Kostencontrolling.', jahreszeit: 'fruehling' },
  { text: 'Jetzt zeigt sich, wer im Winter die Planung gemacht hat.', jahreszeit: 'fruehling' },
  { text: 'Der Boden ist aufgetaut. Die Budgets hoffentlich auch.', jahreszeit: 'fruehling' },
  { text: 'Frühlingserwachen — nicht nur auf der Wiese, auch auf der Baustelle.', jahreszeit: 'fruehling' },
  { text: 'Osterferien sind eine gute Gelegenheit, den Leistungsstand aufzuräumen.', jahreszeit: 'fruehling' },
  { text: 'Im März zählt, was im Oktober versprochen wurde.', jahreszeit: 'fruehling' },
  { text: 'Die Baumblüte erinnert daran: auch Projekte brauchen Zeit zum Reifen.', jahreszeit: 'fruehling' },
  { text: 'Frühling heißt: Baustellen schütteln den Frost ab. Budgets hoffentlich auch.', jahreszeit: 'fruehling' },
  { text: 'Wenn die Störche kommen, stehen die Leistungsphasen 3 auf dem Plan.', jahreszeit: 'fruehling' },
  {
    text: 'Die ersten Sonnenstrahlen auf frischem Estrich — der Moment, in dem Projekte blühen.',
    jahreszeit: 'fruehling',
  },
  { text: 'Frühlingsgefühle auf der Baustelle nennt man auch „Übergabe".', jahreszeit: 'fruehling' },
  { text: 'Jetzt wird gepflanzt. Und dokumentiert.', jahreszeit: 'fruehling' },
  { text: 'Der Frühling ist die Generalprobe für das Baujahr.', jahreszeit: 'fruehling' },
  { text: 'Alles neu macht der Mai. Auch die Kostenprognose.', jahreszeit: 'fruehling' },
  { text: 'Ostern vorbei? Zeit für die LP3-Kostenberechnung.', jahreszeit: 'fruehling' },
  { text: "Frühling auf der Baustelle — jetzt wird's konkret.", jahreszeit: 'fruehling' },
  { text: 'Erste Rohbautage: die Kosten wachsen so schnell wie das Gras.', jahreszeit: 'fruehling' },
  { text: 'Frühjahr heißt: Aus Planung wird Realität.', jahreszeit: 'fruehling' },
  { text: 'Wenn der Mai vorbei ist, sollte die Kostenstand stehen.', jahreszeit: 'fruehling' },

  // --- Sommer (Juni, Juli, August) ----------------------------------------
  { text: 'Beton braucht Schatten. Budgets auch.', jahreszeit: 'sommer' },
  { text: 'Sommerloch? Nicht in der Bauabrechnung.', jahreszeit: 'sommer' },
  { text: 'Wenn die Kräne im Urlaub sind, bleiben Ihnen die Zahlen.', jahreszeit: 'sommer' },
  { text: 'Hochsommer: gute Zeit für Estrich und für ehrliche Kostenprognosen.', jahreszeit: 'sommer' },
  { text: 'Bauurlaub bringt keinen Stillstand — nur verschobene Deadlines.', jahreszeit: 'sommer' },
  { text: 'Sommerhitze auf der Baustelle: der Beton schwitzt, die Kalkulation bleibt cool.', jahreszeit: 'sommer' },
  { text: 'Je höher die Temperatur, desto wichtiger der Schatten im Terminplan.', jahreszeit: 'sommer' },
  { text: 'Im Juli läuft alles. Hoffentlich auch die Kostenerfassung.', jahreszeit: 'sommer' },
  { text: 'Urlaub ist gut. Urlaubsvertretung im Projekt noch besser.', jahreszeit: 'sommer' },
  { text: 'Wer im Sommer gut dokumentiert, feiert im Winter keine Nachtragsorgie.', jahreszeit: 'sommer' },
  { text: 'Hochsommer ist Bauzeit. Und Zeit zum Nachrechnen.', jahreszeit: 'sommer' },
  { text: 'Wenn die Baustelle schließt, bleibt die Abrechnung offen.', jahreszeit: 'sommer' },
  { text: 'Ferien sind kein Grund, den Kostenstand zu vernachlässigen.', jahreszeit: 'sommer' },
  { text: 'August — ein guter Monat für Rohbaufertigstellung und Grillen.', jahreszeit: 'sommer' },
  { text: 'Sonne über der Baustelle ist kein Ersatz für eine saubere Dokumentation.', jahreszeit: 'sommer' },
  { text: 'Sommerzeit ist Hauptbauzeit. Auch für die Excel-Tabelle.', jahreszeit: 'sommer' },
  { text: 'Wenn die Temperaturen steigen, sollten die offenen Punkte sinken.', jahreszeit: 'sommer' },
  { text: 'Ein Eis mit Blick auf den aktuellen Leistungsstand — so geht Sommer.', jahreszeit: 'sommer' },
  { text: 'Sommerferien: zwei Wochen ohne Baubesprechung, dafür mit gutem Dashboard.', jahreszeit: 'sommer' },
  { text: 'Wer im Sommer die Kosten pflegt, ist im Herbst der ruhige Typ.', jahreszeit: 'sommer' },

  // --- Herbst (September, Oktober, November) -------------------------------
  { text: 'Goldener Herbst — nur auf der Fassade, nicht im Nachtragsvolumen.', jahreszeit: 'herbst' },
  { text: 'Wenn die Blätter fallen, fallen hoffentlich auch die offenen Rechnungen.', jahreszeit: 'herbst' },
  { text: 'Oktober: Hoch-Zeit für LP8-Kostenfeststellungen.', jahreszeit: 'herbst' },
  { text: 'Der Herbstwind fegt die Baustelle. Die Abrechnung sortieren Sie selbst.', jahreszeit: 'herbst' },
  { text: 'Wenn die Uhr zurückgestellt wird, läuft die Baustelle trotzdem weiter.', jahreszeit: 'herbst' },
  { text: 'Herbst ist die Zeit der Abnahmen. Und der letzten Nachbesserungen.', jahreszeit: 'herbst' },
  { text: 'Kürbisse und Kostenfeststellungen — beide reifen im Oktober.', jahreszeit: 'herbst' },
  { text: 'Nebel auf der Baustelle, klare Daten im Dashboard.', jahreszeit: 'herbst' },
  { text: 'Die Blätter bunt, die Kostenstufen übersichtlich — so soll es sein.', jahreszeit: 'herbst' },
  { text: 'Im Herbst zeigt sich, was das Jahr gebaut hat.', jahreszeit: 'herbst' },
  { text: 'November ist der Monat der Schlussrechnungen.', jahreszeit: 'herbst' },
  { text: 'Wenn die Tage kürzer werden, wird die To-do-Liste länger.', jahreszeit: 'herbst' },
  { text: 'Frostige Nächte, wärmende Dashboards.', jahreszeit: 'herbst' },
  { text: 'Der Herbst erinnert: sauber abrechnen lohnt sich.', jahreszeit: 'herbst' },
  { text: 'Herbst auf der Baustelle — der Winter hört schon zu.', jahreszeit: 'herbst' },

  // --- Winter (Dezember, Januar, Februar) ---------------------------------
  { text: 'Frosttage sind nicht im Bauzeitenplan. Nachträge schon.', jahreszeit: 'winter' },
  { text: "Wenn's draußen schneit, ist drinnen HOAI. Komfortabel warm.", jahreszeit: 'winter' },
  { text: 'Ein Bauprojekt im Winter ist wie Kaffee ohne Deckel — es wird schneller kalt.', jahreszeit: 'winter' },
  { text: 'Winterpause auf der Baustelle, keine Pause im Controlling.', jahreszeit: 'winter' },
  { text: 'Zwischen den Jahren: der beste Moment für einen Jahresabschluss im Kostenstand.', jahreszeit: 'winter' },
  { text: 'Schneelasten sind statisch. Termindruck ist dynamisch.', jahreszeit: 'winter' },
  { text: 'Januar ist der Monat der guten Vorsätze. Auch für die Dokumentation.', jahreszeit: 'winter' },
  { text: 'Ein Projekt im Winter vorzubereiten ist wie Skifahren — man braucht gute Planung.', jahreszeit: 'winter' },
  { text: 'Adventskerzen brennen schneller nieder als Baufristen.', jahreszeit: 'winter' },
  { text: 'Der Winter ist keine Auszeit, sondern Nachbereitung.', jahreszeit: 'winter' },
  { text: 'Frostgrad und Projektklarheit: beide schätzen wir.', jahreszeit: 'winter' },
  { text: 'Wer im Winter dokumentiert, startet im Frühjahr geordnet.', jahreszeit: 'winter' },
  { text: 'Neujahr, neue Kostenstufe.', jahreszeit: 'winter' },
  { text: 'Wintertage sind kurz. Terminpläne nicht.', jahreszeit: 'winter' },
  { text: 'Der Dezember bringt Geschenke. Und die letzten Schlussrechnungen.', jahreszeit: 'winter' },
];

function aktuelleTageszeit(): Tageszeit {
  const h = new Date().getHours();
  if (h >= 5 && h <= 10) return 'morgen';
  if (h >= 11 && h <= 16) return 'tag';
  if (h >= 17 && h <= 22) return 'abend';
  return 'nacht';
}

function aktuelleJahreszeit(): Jahreszeit {
  // Meteorologische Jahreszeiten: März–Mai = Frühling usw.
  const m = new Date().getMonth(); // 0..11
  if (m >= 2 && m <= 4) return 'fruehling';
  if (m >= 5 && m <= 7) return 'sommer';
  if (m >= 8 && m <= 10) return 'herbst';
  return 'winter';
}

/**
 * Liefert einen zufälligen Spruch, der zur aktuellen Tageszeit und/oder
 * Jahreszeit passt. Generische Sprüche (ohne Tags) sind immer dabei, damit
 * der Pool auch ungewöhnliche Uhrzeiten (z.B. 03 Uhr im Sommer) trifft.
 */
export function zufaelligerSlogan(): string {
  const tz = aktuelleTageszeit();
  const jz = aktuelleJahreszeit();
  const pool = SLOGANS.filter((s) => {
    const tzMatch = s.tageszeit === undefined || s.tageszeit === tz;
    const jzMatch = s.jahreszeit === undefined || s.jahreszeit === jz;
    return tzMatch && jzMatch;
  });
  if (pool.length === 0) {
    return SLOGANS[0].text;
  }
  const idx = Math.floor(Math.random() * pool.length);
  return pool[idx].text;
}
